import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendUrl';
import { getOrCreateDeviceFingerprint, getDeviceLabel, clearDeviceFingerprint } from '../utils/deviceFingerprint';

const API_URL = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('rf_token'));
  const [loading, setLoading] = useState(true);

  const fetchPermissions = async (authToken) => {
    try {
      const response = await axios.get(`${API_URL}/auth/me/permissions`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setPermissions(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          await fetchPermissions(token);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('rf_token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  // iter239 — silent profile refresh (used for realtime permission changes)
  const refreshUser = async () => {
    try {
      const tok = localStorage.getItem('rf_token');
      if (!tok) return;
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${tok}` }
      });
      setUser(response.data);
    } catch { /* keep current session on transient errors */ }
  };

  const login = async (email, password) => {
    // iter 156 — 2FA flow:
    //   • Backend returns { requires_otp: true, challenge_id } when MFA is
    //     enforced for this account. The caller must then prompt the user
    //     for the OTP and call `verifyOtp(challenge_id, code)` to finalise.
    //   • When MFA is bypassed (admin escape hatch / MFA_ENFORCED=false)
    //     the legacy { token, user } shape is returned and we log the user
    //     in immediately — preserving full backward compatibility.
    // iter 167 — Pass the locally-stored device fingerprint. If the backend
    // recognises it (non-revoked, non-expired trust row) the OTP step is
    // skipped entirely and we get the JWT directly. This is the
    // "never get logged out on refresh" guarantee.
    const device_fingerprint = getOrCreateDeviceFingerprint();
    const response = await axios.post(`${API_URL}/auth/login`, {
      email, password, device_fingerprint,
    });
    if (response.data?.requires_otp) {
      return { requiresOtp: true, ...response.data };
    }
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('rf_token', newToken);
    setToken(newToken);
    setUser(userData);
    await fetchPermissions(newToken);
    return { requiresOtp: false, user: userData };
  };

  // iter 156 — Complete the 2FA login. Used by <OtpVerifyDialog/>.
  // iter 167 — Accepts `rememberDevice` so the dialog's "Remember this
  // device" checkbox can persist the trust on the server side.
  const verifyOtp = async (challengeId, code, { rememberDevice = true } = {}) => {
    const device_fingerprint = getOrCreateDeviceFingerprint();
    const response = await axios.post(`${API_URL}/auth/verify-otp`, {
      challenge_id: challengeId,
      code,
      remember_device: !!rememberDevice,
      device_fingerprint,
      device_label: getDeviceLabel(),
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('rf_token', newToken);
    setToken(newToken);
    setUser(userData);
    await fetchPermissions(newToken);
    return userData;
  };

  // iter 156 — Request a fresh OTP for the active challenge (cooldown-gated
  // by the backend — UI shows a countdown).
  const resendOtp = async (challengeId) => {
    const response = await axios.post(`${API_URL}/auth/resend-otp`, {
      challenge_id: challengeId,
    });
    return response.data;
  };

  const register = async (email, name, password, role) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, name, password, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('rf_token', newToken);
    setToken(newToken);
    setUser(userData);
    await fetchPermissions(newToken);
    return userData;
  };

  const acceptInvite = async (inviteToken, password) => {
    const response = await axios.post(`${API_URL}/invites/accept`, { token: inviteToken, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('rf_token', newToken);
    setToken(newToken);
    setUser(userData);
    await fetchPermissions(newToken);
    return userData;
  };

  const logout = () => {
    // iter 156 — Best-effort server-side revocation so the JWT can't be
    // reused if it was leaked elsewhere. We don't wait for the response;
    // the client-side cleanup proceeds regardless.
    // iter 167 — The same endpoint also revokes the trusted-device row
    // for this user. We deliberately clear the local fingerprint too so
    // the next login on this browser starts as a fresh "untrusted" device.
    if (token) {
      axios.post(`${API_URL}/auth/logout`, null, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    localStorage.removeItem('rf_token');
    clearDeviceFingerprint();
    setToken(null);
    setUser(null);
    setPermissions(null);
  };

  // Helper functions for permission checking
  const hasTabAccess = (tabName) => {
    if (!permissions) return false;
    return permissions.tab_visibility?.[tabName] === true;
  };

  const hasObjectPermission = (objectName, permission) => {
    if (!permissions) return false;
    const perms = permissions.object_permissions?.[objectName] || [];
    return perms.includes(permission);
  };

  const value = {
    user,
    token,
    permissions,
    loading,
    login,
    verifyOtp,   // iter 156
    resendOtp,   // iter 156
    register,
    acceptInvite,
    logout,
    refreshUser,
    hasTabAccess,
    hasObjectPermission,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isTeamLead: user?.role === 'team_lead',
    isICF: user?.role === 'icf',
    isBDE: user?.role === 'bde',
    isBDEManager: user?.role === 'bde_manager',
    isRecruiter: user?.role === 'recruiter',
    isRecruiterManager: user?.role === 'recruiter_manager', // iter 140
    isHR: user?.role === 'hr',
    isOperations: user?.role === 'operations',
    isITSupport: user?.role === 'it_support',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
